/**
 * LOOP LAB — CLAUDECODE101 chapter 1, the loop you already know, running for real.
 *
 * FOUNDER, 2026-08-15: "add a module in end for mastering claudecode zero to hero" ·
 * "ensure course is world class and not paper and texty".
 *
 * ── THE LEARNER WALKS A REAL TRANSCRIPT ───────────────────────────────────────────────────────
 * Thirty consecutive steps from the session that built the CodeAI101 labs. Step through it and the
 * shape appears on its own: long runs of tool calls, occasional stops to reason, and one place
 * where a human walks in. No diagram of a loop — a loop, with the timestamps taken off it.
 *
 * ── THE NUMBER THAT CONFIRMS HIS OWN FIGURE ───────────────────────────────────────────────────
 * 576 tool calls over 16 prompts is 36.0 per prompt, and CodeAI101 §5 says "~25-50 tool calls per
 * task". He wrote that before this session existed. The lab computes both and checks the overlap
 * live, so if a re-count ever falls outside the range it will say so rather than keep claiming a
 * tidy result.
 *
 * ── NAMES AND COUNTS ONLY ─────────────────────────────────────────────────────────────────────
 * No commands, no arguments, no file paths, no output. A transcript holds whatever the work
 * touched; shipping any of it into a public bundle to make a teaching point would be indefensible.
 */

import { useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { cited, computed, refused } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  MEASURED, REMEASURED, WINDOW, WINDOW_IS_REAL, HIS_LOOP, HIS_LIMIT, HIS_RANGE, THE_POINT,
  THE_DIAL_POSITION, perPrompt, perPromptNow, landsInHisRange, landsNow, roleOf,
  A_RATIO_IS_NOT_A_FLOOR, THE_DENOMINATOR, WHY_IT_MOVED, THE_CONFIRMATION_DID_NOT_SURVIVE,
  SOURCE_SESSION, SOURCE_CH3, SOURCE_CH4, SOURCE_CH4_LIMIT,
} from './ccloop.mjs';

export default function LoopLab() {
  const [at, setAt] = useState(0);
  const shown = WINDOW.slice(0, at);
  const current = at > 0 ? WINDOW[at - 1] : null;
  const toolsSoFar = shown.filter((s) => s !== 'you' && s !== 'assistant').length;

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Loop lab · a real session</div>
      <h2 className="g-h2 lab-h">You already know this loop</h2>
      <p className="g-body"><Value of={cited(HIS_LOOP, SOURCE_CH4)} /></p>
      <p className="g-body">{THE_POINT}</p>

      {/* ── WALK THE REAL WINDOW ── */}
      <div className="lab-task">
        <div className="g-label">Thirty consecutive steps from the session that built this course</div>
        <div className="lab-task-head">
          <button className="g-btn g-btn-primary" disabled={at >= WINDOW.length}
            onClick={() => setAt((n) => Math.min(WINDOW.length, n + 1))}>Step</button>
          <button className="g-btn" disabled={at >= WINDOW.length} onClick={() => setAt(WINDOW.length)}>Run it out</button>
          <button className="g-btn" onClick={() => setAt(0)}>Reset</button>
          <span className="cou-ms">{at} of {WINDOW.length} · {toolsSoFar} tool calls</span>
        </div>
        <div className="ccl-strip">
          {WINDOW.map((s, i) => (
            <button key={i} className={`ccl-step ccl-${roleOf(s).kind} ${i < at ? 'ccl-done' : ''} ${i === at - 1 ? 'ccl-now' : ''}`}
              onClick={() => setAt(i + 1)} title={`step ${i + 1}`}>
              {s === 'you' ? 'you' : s === 'assistant' ? '·' : s[0]}
            </button>
          ))}
        </div>
        {current && (
          <div className={`ccl-now-box ccl-${roleOf(current).kind}-box`}>
            <div className="g-label">step {at} · {roleOf(current).who}</div>
            <p className="g-body">{roleOf(current).says}</p>
            {current === 'you' && (
              <p className="g-body ccl-human">
                <Value of={cited('The agent runs its loop without per-step approval, but you are watching — logs streaming, dashboards updating, you can interrupt at any time.', SOURCE_CH4)} />
                {' '}That sentence, happening.
              </p>
            )}
          </div>
        )}
        <p className="g-body g-dim">{WINDOW_IS_REAL}</p>
      </div>

      {/* ── THE WHOLE SESSION, COUNTED ── */}
      <div className="lab-task">
        <div className="g-label">The session, counted from its transcript — at least this many</div>
        <div className="lab-readout">
          <div><span className="g-label">Records (min)</span><Value of={computed(String(MEASURED.records))} /></div>
          <div><span className="g-label">Your prompts (min)</span><Value of={computed(String(MEASURED.prompts))} /></div>
          <div><span className="g-label">Tool calls (min)</span><Value of={computed(String(MEASURED.tool_calls))} /></div>
        </div>
        {/* The rate is NOT in the readout above, and that is a correction rather than a layout
            choice. It sat under "at least this many" while being a quotient of two floors, which is
            not a floor — and the re-count proved it by coming in five times lower. */}
        {MEASURED.by_tool.map((t) => (
          <div key={t.name} className="ccl-tool">
            <code>{t.name}</code>
            <div className="ccl-bar" style={{ width: `${(t.n / MEASURED.tool_calls) * 100}%` }} />
            <span className="cou-ms">{t.n}</span>
            <span className="ccl-what">{t.what}</span>
          </div>
        ))}
        <p className="g-body ccl-floors">
          <b>These are floors, not totals.</b> {MEASURED.why_floors} The session was still writing to
          its own transcript while these were read — you cannot take a still measurement of a loop
          from inside it, which is this chapter's lesson arriving early.
        </p>
        <p className="g-body g-dim">
          <Value of={cited('the counts above', SOURCE_SESSION)} /> — tool names and counts only.
          No commands, no arguments, no output.
        </p>
      </div>

      {/* ── THE RATE, AND WHAT HAPPENED WHEN IT WAS COUNTED TWICE ── */}
      <div className="lab-task">
        <div className="g-label">The rate — a ratio, not a floor</div>
        <div className="lab-readout">
          <div><span className="g-label">On {MEASURED.counted_on}</span><Value of={computed(`${perPrompt()} per prompt`)} /></div>
          <div><span className="g-label">On {REMEASURED.counted_on}</span><Value of={computed(`${perPromptNow()} per prompt`)} /></div>
        </div>
        <p className="g-body ccl-floors"><b>A ratio of two floors is not a floor.</b> {A_RATIO_IS_NOT_A_FLOOR}</p>
        <p className="g-body g-dim"><b>And the denominator is a choice.</b> {THE_DENOMINATOR}</p>
      </div>

      {/* ── THE CONFIRMATION THAT DID NOT SURVIVE BEING CHECKED AGAIN ── */}
      <div className="lab-task">
        <div className="g-label">A figure from CodeAI101, checked against real work — twice</div>
        <p className="g-body"><Value of={cited(HIS_LIMIT, SOURCE_CH4_LIMIT)} /></p>
        <div className={`ccl-verdict ${landsInHisRange() ? 'ccl-hit' : ''}`}>
          <Value of={computed(`${perPrompt()} per prompt on ${MEASURED.counted_on}`)} />
          <span className="ccl-vtext">
            {landsInHisRange()
              ? ` — inside his stated range of ${HIS_RANGE[0]}–${HIS_RANGE[1]}, from a session he had never seen. This lab announced that, and stopped there.`
              : ` — outside his stated ${HIS_RANGE[0]}–${HIS_RANGE[1]}.`}
          </span>
        </div>
        <div className={`ccl-verdict ${landsNow() ? 'ccl-hit' : ''}`}>
          {landsNow()
            ? <><Value of={computed(`${perPromptNow()} per prompt on ${REMEASURED.counted_on}`)} />
              <span className="ccl-vtext"> — still inside the range.</span></>
            : <><Value of={refused(`${perPromptNow()} per prompt on ${REMEASURED.counted_on} is OUTSIDE his stated ${HIS_RANGE[0]}–${HIS_RANGE[1]}`)} />
              <span className="ccl-vtext"> — the re-count no longer confirms the figure, and saying so is the point of computing it rather than asserting it.</span></>}
        </div>
        <p className="g-body ccl-floors">{THE_CONFIRMATION_DID_NOT_SURVIVE}</p>
        <p className="g-body g-dim">{WHY_IT_MOVED}</p>
      </div>

      {/* ── WHERE THIS SESSION SAT ON THE DIAL ── */}
      <div className="lab-task">
        <div className="g-label">Where this session sat on chapter 4's dial</div>
        <p className="g-body"><b>Evidence:</b> {THE_DIAL_POSITION.evidence}</p>
        <p className="g-body">{THE_DIAL_POSITION.reading}</p>
        <p className="g-body g-dim">{THE_DIAL_POSITION.is_a_reading}</p>
      </div>

      <p className="g-body g-dim lab-why">
        Every number here was counted from a real transcript on this machine, and a gate re-counts
        them from the same file — so if a figure drifts, it goes red rather than staying quotable.
        <br />
        What is deliberately missing: the commands, their arguments, the files they touched and
        everything they printed. A transcript holds whatever the work touched — source,
        configuration, occasionally a secret. The shape of the loop is the lesson here; the contents
        are not needed for it, so they are not in this page.
        <br />
        <Value of={cited('a tool call is something the AI decides to make and a harness actually runs', SOURCE_CH3)} />
      </p>
    </div>
  );
}
